import { useState, useEffect, useRef } from "react";
import { FaBold, FaItalic, FaUnderline, FaTimes, FaReply, FaEdit, FaSave, FaSuperscript, FaSubscript, FaPaperPlane, FaPaperclip, FaRegTrashAlt, FaFileAlt, FaChevronDown } from "react-icons/fa";
import { Button } from "react-bootstrap";
import { toastError, toastSuccess } from '../toaster';
import pathconfig from '../../pathconfig.json';
import axios from "axios";
import NodataFound from "../nodata";
import packjson from '../../../package.json';
import { useAppContextProvider } from '../../contextapi';
import CommentBox from "./commments/CommentBox";
import { TbH2 } from "react-icons/tb";

// Media and Image attachments must each be one of these file types.
const ALLOWED_MEDIA_EXTENSIONS = ['ttml', 'txt', 'vtt', 'mp4', 'mp3', 'gif'];
const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'tif', 'tiff', 'eps', 'svg'];
const getFileExtension = (filename) => (filename.split('.').pop() || '').toLowerCase();
const isValidMediaFile = (filename) => ALLOWED_MEDIA_EXTENSIONS.includes(getFileExtension(filename));
const isValidImageFile = (filename) => ALLOWED_IMAGE_EXTENSIONS.includes(getFileExtension(filename));

// Display label for an Image tab file, mirroring the Image tab's own modal
// title (getImageTitle in imagelist.js) — e.g. "Fig. 4 (gr4.pdf)",
// "Inline Image (fx1.pdf)" — so the "Select Image to Replace" list matches
// what the Master Copier actually sees when they open that image.
const IMAGE_TYPE_MAP = {
  gr: "Graphical Image",
  ga: "Graphical Abstract Image",
  fx: "Inline Image",
  mmc: "Supplementary videos and gifs",
};
const getImageDisplayTitle = (name) => {
  if (!name) return "";
  const baseName = name.split(".")[0];
  const match = baseName.match(/^([a-zA-Z]+)(\d+)$/);
  const prefix = match?.[1]?.toLowerCase();
  const number = match?.[2];
  if (prefix === "gr") return `Fig. ${number} (${name})`;
  if (prefix === "sc") return `Scheme ${number}`;
  if (prefix === "pl") return `Plate ${number}`;
  const fullName = IMAGE_TYPE_MAP[prefix] || "";
  return fullName ? `${fullName} (${name})` : name;
};

const QueryTab = (props) => {
  const [query, setQuery] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedQuery, setEditedQuery] = useState("");
  const [replyIndex, setReplyIndex] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [fileAttachment, setFileAttachment] = useState([]);
  const [fileAllData, setFileAllData] = useState([]);
  const [queryFile, setQueryFile] = useState();
  const [queryStatus, setQueryStatus] = useState(false);
  const [queryData, setQueryData] = useState({})
  const fileInputRef = useRef(null);
  const [loader, setLoader] = useState(false);
  const [render, setRender] = useState(false);
  const [answerCount, setAnswerCount] = useState(0);
  const { userProfile, setLineFocus, authorQuery, setAuthorQuery, setAuthorQueryChange, setSidebarClose, viewDoc, alttextEnabled, fullData } = useAppContextProvider();
  const replyContentRef = useRef(null);
  const [commentProgress, setCommentProgress] = useState(false)
  // Staged-attachment flow (mirrors the comment tab): files are held here until
  // the reply is posted, so the user can pick a type / alt-text option first.
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState('');
  const [altTextGeneration, setAltTextGeneration] = useState(''); // 'yes' | 'no' | ''
  const [imageAction, setImageAction] = useState(''); // 'new' | 'replace' | ''
  const [replaceImageName, setReplaceImageName] = useState('');
  const [mediaAction, setMediaAction] = useState(''); // 'new' | 'replace' | ''
  const [replaceMediaName, setReplaceMediaName] = useState('');
  const [imageFileList, setImageFileList] = useState([]);
  const [supplementaryFileList, setSupplementaryFileList] = useState([]);
  const [replaceDropdownOpen, setReplaceDropdownOpen] = useState(false);
  const replaceDropdownRef = useRef(null);
  const [imageTypeDropdownOpen, setImageTypeDropdownOpen] = useState(false);
  const imageTypeDropdownRef = useRef(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const replyBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  let docId;

  useEffect(() => {
    // Focus the close button when Author Query tab is opened
    if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [closeBtnRef]);

  useEffect(() => {
    // Focus the Reply button when reply area is opened
    if (replyIndex !== null && replyBtnRef.current) {
      replyBtnRef.current.focus();
    }
  }, [replyIndex]);

  // Fetch the list of images from the Image tab's folder so the "Replace Image"
  // dropdown can offer the exact existing image names to attach against.
  useEffect(() => {
    if (!fullData?.Content) return;
    const regex = /(?<!.)\\graphicspath\{\{([^}]*)\}\}/;
    const match = fullData.Content.match(regex);
    if (!match) return;
    const folderPath = match[1].replace(/\/$/, "");

    const getImageFileList = async () => {
      try {
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].get_image_details}`;
        const response = await axios.post(url, { file_path: folderPath });
        setImageFileList(response.data.Files || []);
      } catch (error) {
        console.log(error);
      }
    };
    getImageFileList();
  }, [fullData]);

  // Fetch the list of Supplementary tab files so the "Replace File" dropdown
  // (Media attachments) can offer the exact existing file names to attach against.
  useEffect(() => {
    if (!userProfile?.docid) return;

    const getSupplementaryFileList = async () => {
      try {
        const GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
        const GetDetailData = JSON.parse(GetDetailDataString);
        if (!GetDetailData?.Articledetails?.ArtResourcePath) return;

        const basePath = `${GetDetailData.Articledetails.ArtResourcePath}/Supplementary`;
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].retrievefiles}`;
        const response = await axios.post(url, { InputPath: basePath });

        const filteredFiles = (response.data || [])
          .filter((f) => !f.path.replace(basePath + "/", "").includes("/"))
          .map((f) => ({ name: f.path.split('/').pop() }));

        setSupplementaryFileList(filteredFiles);
      } catch (error) {
        console.log(error);
      }
    };
    getSupplementaryFileList();
  }, [userProfile]);

  // Close the "Select Image/File to Replace" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!replaceDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (replaceDropdownRef.current && !replaceDropdownRef.current.contains(e.target)) {
        setReplaceDropdownOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setReplaceDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [replaceDropdownOpen]);

  // Close the "Image Type" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!imageTypeDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (imageTypeDropdownRef.current && !imageTypeDropdownRef.current.contains(e.target)) {
        setImageTypeDropdownOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setImageTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [imageTypeDropdownOpen]);

  useEffect(() => {
    docId = userProfile.docid;
    getDetails();
  }, [])
  const handleError = (error, message = error || 'Something went wrong') => {
    console.error(error);
    toastError(message);
  };
  useEffect(() => {
    try {
      if (authorQuery.length !== undefined) {
        const sortedData = authorQuery.sort((a, b) => {
          const numA = parseInt(a.label.replace(/\D/g, ''));
          const numB = parseInt(b.label.replace(/\D/g, ''));
          return numA - numB;
        });
        sortedData.map((item) => item.ActivityID === undefined ? userProfile.ActivityID : item.ActivityID)
        setQuery(sortedData);
      } else {
        setQuery([])
      }
    } catch (error) {
      handleError(error, 'Error processing queries');
    }
  }, [authorQuery]);

  useEffect(() => {
    if (query) {
      if (query.length > 0) {
        // setAuthorQuery(query);
        let answerFilter = query.filter((a) =>
          a?.answerCheck === 'yes' ||
          a?.repliesMark === true ||
          (a?.answerCheck === 'no' && a?.replies?.length > 0) ||
          a?.replied === true ||
          a?.reviewed === true ||
          a?.verified === true
        );
        setAnswerCount(answerFilter.length);
      }
    }
  }, [query]);



  const queryScroll = (number) => {
    setLineFocus(number);
  };

  const handleSave = (lineNumber, wholeMatch, editedContent) => {
    const querychange = { linenumber: lineNumber, oldcontent: wholeMatch, changecontent: editedContent };
    setAuthorQueryChange(querychange);
    const updatedQuery = query.map((item, index) =>
      index === editingIndex ? { ...item, query: editedContent, wholematch: wholeMatch } : item
    );
    setQuery(updatedQuery); // Update the state with the new query
    setEditingIndex(null); // Reset editing state
    setEditedQuery(""); // Clear edited query state
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedQuery("");
    setReplyIndex(null); // Reset reply state
    setReplyContent(""); // Clear reply content state
    setFileAttachment([]); // Clear file attachments
    resetAttachmentStaging();

    // Reset the "yes" and "no" radio input buttons for the particular index
    const updatedQuery = query.map((item, index) => {
      if (index === replyIndex) {
        return {
          ...item,
          answerCheck: undefined,
          repliesMark: false,
          showReplyArea: false,
          showReplyButton: true,
          replied: false,
        };
      }
      return item;
    });
    setQuery(updatedQuery);
    setAuthorQuery(updatedQuery);
  };

  // Clear the staged-attachment selection (file, type and alt-text choice).
  const resetAttachmentStaging = () => {
    setSelectedFiles([]);
    setAttachmentTypes('');
    setAltTextGeneration('');
    setImageAction('');
    setReplaceImageName('');
    setMediaAction('');
    setReplaceMediaName('');
    setIsFileUploaded(false);
  };

  const handleReply = ( index) => {
    setReplyIndex(index);
    setEditingIndex(null); // Cancel editing mode if replying
    setFileAttachment([]); // Clear previous file attachments
    setQueryFile(); // Reset query file
    resetAttachmentStaging();
  };

  const handleReplySave = async (index) => {
    console.log('<<<handleReplySave called>>>', index);
    const replyContentHtml = replyContentRef.current.innerHTML;

    // Improved content validation
    const cleanContent = replyContentHtml
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/&nbsp;/g, " ") // Convert nbsp to regular spaces
      .replace(/\s+/g, " ")    // Normalize multiple spaces
      .trim();                 // Remove leading/trailing spaces

    if (!cleanContent) {
      toastError("Reply content cannot be empty");
      return;
    }

    // Upload any staged attachments before saving the reply.
    const uploadedFiles = await uploadStagedFiles();
    if (uploadedFiles === null) return; // validation failed

    const replyWithRole = {
      content: replyContentHtml,
      role: userProfile.role,
      timestamp: new Date().toISOString()
    };

    const updatedQuery = query.map((item, idx) =>
      idx === index ? {
        ...item,
        replies: [...(item.replies || []), replyWithRole],
        attachment: [...(item.attachment || []), ...uploadedFiles],
        repliesMark: false,
        showReplyArea: false,
        replied: true
      } : item
    );

    setQuery(updatedQuery);
    setAuthorQuery(updatedQuery); // Update the authorQuery state
    setReplyIndex(null);
    setReplyContent("");
    setFileAttachment([]);
    setQueryFile();
    resetAttachmentStaging();
  };


  const applyStyle = (command) => {
    const currentSelection = window.getSelection();
    if (currentSelection.rangeCount > 0) {
      const range = currentSelection.getRangeAt(0);
      const parentElement = range.commonAncestorContainer.parentNode;
      if (command === 'superscript' || command === 'subscript') {
        if (parentElement.tagName.toLowerCase() === (command === 'superscript' ? 'sup' : 'sub')) {
          const grandparent = parentElement.parentNode;
          while (parentElement.firstChild) {
            grandparent.insertBefore(parentElement.firstChild, parentElement);
          }
          grandparent.removeChild(parentElement);
        } else {
          document.execCommand(command, false, null);
        }
      } else {
        document.execCommand(command, false, null);
      }
    }
  };

  // Stage the picked file – the actual upload is deferred until the reply is
  // posted (same process as the comment tab).
  const handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return; // Exit if no file is selected (e.g. dialog cancelled)
    }
    const maxFileSize = 40 * 1024 * 1024;
    if (file.size > maxFileSize) {
      toastError('The file size should not exceed more than 40 MB');
      e.target.value = null;
      setIsFileUploaded(false);
      return;
    }
    if (fileAllData.some((item) => item.Filename === file.name)) {
      toastError(`${file.name} filename already uploaded. Please rename and retry.`);
      e.target.value = null;
      return;
    }
    setSelectedFiles((prev) => [...prev, {
      file: file,
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB'
    }]);
    setIsFileUploaded(true);
    e.target.value = null;
  };

  // Upload a single staged file. Returns the stored attachment record, or null.
  const handleFileupload = async (filename) => {
    setLoader(true);
    try {
      if (fileAllData.some((item) => item.Filename === filename.name)) {
        toastError(`${filename.name} filename already uploaded. Please rename and retry.`);
        setLoader(false);
        return null;
      }

      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      const formdata = new FormData();
      formdata.append("jobGUID", GetDetailData.Articledetails.ArticleGUID);
      formdata.append("ActivityID", GetDetailData.ActivtyDetails.ActivityID);
      formdata.append("docid", userProfile.docid);
      formdata.append("type", "commentAttachment");
      formdata.append("files", filename);
      formdata.append("fileTypes[0]", attachmentTypes);
      formdata.append("fileComments[0]", 'AuthorQuery reply Attachment');
      formdata.append("altTextGeneration[0]", alttextEnabled && attachmentTypes === "imageAttachment" ? altTextGeneration : null);
      formdata.append("imageAction[0]", attachmentTypes === "imageAttachment" ? imageAction : null);
      formdata.append("replaceImageName[0]", attachmentTypes === "imageAttachment" && imageAction === "replace" ? replaceImageName : null);
      formdata.append("mediaAction[0]", attachmentTypes === "mediaAttachment" ? mediaAction : null);
      formdata.append("replaceMediaName[0]", attachmentTypes === "mediaAttachment" && mediaAction === "replace" ? replaceMediaName : null);

      const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].upload_attachment}`;
      const response = await axios.post(url, formdata, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.SuccessFiles?.length) {
        response.data.SuccessFiles.forEach((item) =>
          toastSuccess(`${item.data[0].Filename} - ${item.message}`)
        );
        const updatedFiles = await getDetails(filename);
        props.refreshAttachments();
        setLoader(false);
        return updatedFiles?.[0] || null;
      }
      if (response?.data?.FailedFiles?.length) {
        response.data.FailedFiles.forEach((item) =>
          toastError(`${item.data[0].Filename} - ${item.message}`)
        );
      }
      setLoader(false);
      return null;
    } catch (error) {
      console.log(error);
      toastError(error.message);
      setLoader(false);
      return null;
    }
  };

  // Validate + upload every staged file. Returns the uploaded records, or null
  // when validation fails so the caller can abort the reply save.
  const uploadStagedFiles = async () => {
    if (selectedFiles.length === 0) return [];

    for (const staged of selectedFiles) {
      if (!attachmentTypes) {
        toastError(`Please select an attachment type for ${staged.name}`);
        return null;
      }
      if (attachmentTypes === 'mediaAttachment' && !isValidMediaFile(staged.name)) {
        toastError(`"${staged.name}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
        return null;
      }
      if (attachmentTypes === 'imageAttachment' && !isValidImageFile(staged.name)) {
        toastError(`"${staged.name}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
        return null;
      }
      if (alttextEnabled && attachmentTypes === 'imageAttachment' && !altTextGeneration) {
        toastError(`Please select an alternative text option for ${staged.name}`);
        return null;
      }
      if (attachmentTypes === 'imageAttachment' && !imageAction) {
        toastError(`Please select an image type for ${staged.name}`);
        return null;
      }
      if (attachmentTypes === 'imageAttachment' && imageAction === 'replace' && !replaceImageName) {
        toastError(`Please select the image to replace for ${staged.name}`);
        return null;
      }
      if (attachmentTypes === 'mediaAttachment' && !mediaAction) {
        toastError(`Please select a file type for ${staged.name}`);
        return null;
      }
      if (attachmentTypes === 'mediaAttachment' && mediaAction === 'replace' && !replaceMediaName) {
        toastError(`Please select the file to replace for ${staged.name}`);
        return null;
      }
    }

    const uploaded = [];
    fileProgress(true);
    for (const staged of selectedFiles) {
      const record = await handleFileupload(staged.file);
      if (record) uploaded.push(record);
    }
    fileProgress(false);
    return uploaded;
  };

  const getDetails = async (filename) => {
    try {
      props.getTrigger(false)
      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      const getUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].getAttachment}`;
      const body = {
        "Proof_Type": GetDetailData.jobdetails.ProofType,
        "JobGUID": GetDetailData.jobdetails.JobGUID,
        "docid": userProfile.docid
      }
      const response = await axios.post(getUrl, body);
      let dataChange = response.data.data.map((item) => {
        return {
          ...item,
          filetype: item.Filename.split('.')[1]
        }
      })
      let commentType = dataChange.filter((item) =>
        item.AttachType === 'commentAttachment' ||
        item.AttachType === 'imageAttachment' ||
        item.AttachType === 'textAttachment' ||
        item.AttachType === 'mediaAttachment' ||
        item.AttachType === 'otherAttachment'
      )
      props.getTrigger(true)
      setLoader(false)
      if (filename) {
        let files = commentType.filter((item) => item.Filename === filename.name)
        setFileAttachment(files)
        return files
      } else {
        setFileAllData(commentType)
        return commentType
      }
    } catch (error) {
      props.getTrigger(true);
      setLoader(false);
      toastError(error.message);
      return null;
    }
  }

  const deleteConfirm = (value, index, parentIndex) => {
    try {
      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      let data = {
        "ArticleGUID": value.ArticleGUID,
        "AttachID": value.AttachID,
        "Proof_Type": GetDetailData.jobdetails.ProofType,
        "AttachFlag": value.AttachFlag,
        "docid": userProfile.docid
      }
      const deleteUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].deleteAttachment}`
      axios.post(deleteUrl, data).then((response) => {
        toastSuccess(`${response.data.data[0].Filename} - ${response.data.message}`)
        props.refreshAttachments(); 
        getDetails()
        setFileAttachment([])
        if (index !== null) {
          let FileDetails = [...query];
          FileDetails[parentIndex].attachment.splice(index, 1)
          setQuery(FileDetails);
        }
        setQueryFile()
      }).catch((error) => {
        console.log(error);
        toastError(error.message);
      })
    } catch (error) {
      handleError(error, 'Error deleting attachment');
    }
  }

  const handleMark = (idvalue) => {
    const updatedQuery = query.map((item, idx) =>
      idx === idvalue ? {
        ...item,
        repliesMark: !item.repliesMark,
        showReplyButton: !item.repliesMark, // Toggle reply button visibility
        replies: [] // Clear any existing replies when marking no action
      } : item
    );
    setQuery(updatedQuery);
    setAuthorQuery(updatedQuery); // Update the authorQuery state
  };

  // Toggle the "Reviewed" acknowledgement for notice-type (reviewOnly) queries.
  const handleReviewed = (idvalue) => {
    const updatedQuery = query.map((item, idx) =>
      idx === idvalue ? { ...item, reviewed: !item.reviewed } : item
    );
    setQuery(updatedQuery);
    setAuthorQuery(updatedQuery); // Update the authorQuery state
  };

  // Toggle the "Verified" acknowledgement for auto-generated alt-text queries.
  const handleVerified = (idvalue) => {
    const updatedQuery = query.map((item, idx) =>
      idx === idvalue ? { ...item, verified: !item.verified } : item
    );
    setQuery(updatedQuery);
    setAuthorQuery(updatedQuery); // Update the authorQuery state
  };


  const editReply = ( id, replyId) => {
    const replyContent = query[id].replies[replyId].content || '';
    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = replyContent;

    setQueryData({
      'value': contentDiv.innerHTML || '',
      'id': replyId,
      'parentID': id
    });
    setQueryStatus(true);
  }

  const handleReplySaved = async (id, replyId) => {
    const replyContentHtml = document.getElementById("TypeContentEdit").innerHTML;

    // Remove HTML tags and check if content has only spaces or empty HTML
    const plainText = replyContentHtml
      .replace(/<[^>]*>/g, "") // Remove all HTML tags
      .replace(/&nbsp;/g, ""); // Remove non-breaking spaces

    if (!plainText.trim()) {
      toastError("Reply content cannot be empty or only spaces");
      return;
    }

    // Upload any staged attachments before saving the edited reply.
    const uploadedFiles = await uploadStagedFiles();
    if (uploadedFiles === null) return; // validation failed

    let replyArray = [...query]
    replyArray[id].replies[replyId] = {
      content: replyContentHtml,
      role: replyArray[id].replies[replyId].role,
      timestamp: replyArray[id].replies[replyId].timestamp
    };
    if (uploadedFiles.length) {
      replyArray[id].attachment = [...(replyArray[id].attachment || []), ...uploadedFiles];
    }
    setQuery(replyArray)
    setAuthorQuery(replyArray)
    setQueryData({ 'value': '', 'id': '' })
    setQueryStatus(false)
    resetAttachmentStaging();
  };

  const editClose = () => {
    setQueryData({ 'value': '', 'id': '' })
    setQueryStatus(false)
    resetAttachmentStaging()
  }
  const replyDelete = (id, replyId) => {
    let replyArray = [...query];
    replyArray[id].replies.splice(replyId, 1);

    // Show both options when reply is deleted
    if (replyArray[id].replies.length === 0) {
      replyArray[id].repliesMark = false;
      replyArray[id].showReplyButton = replyArray[id].aqCheck ? false : true; // Hide for aqCheck, show for regular
      replyArray[id].answerCheck = undefined; // Reset radio selection
      replyArray[id].replied = false; // Reset replied state
      replyArray[id].hasReplies = false;
    }
    setQuery(replyArray);
  }

  const handleRadio = (e, id) => {
    try {
      const radioValue = e.target.value;
      const updatedQuery = query.map((item, idx) =>
        idx === id ? {
          ...item,
          answerCheck: radioValue,
          repliesMark: radioValue === 'yes' ? true : false,
          previousReply: radioValue === 'yes' ? item.replies : item.previousReply,
          replies: radioValue === 'yes' ? [] : (item.replies || []),
          showReplyArea: radioValue === 'no'
        } : item
      );

      if (radioValue === 'no') {
        if (!query[id].replies || query[id].replies.length === 0) {
          setReplyIndex(id);
        } else {
          setQueryData({
            'value': query[id].replies[0],
            'id': 0,
            'parentID': id
          });
          setQueryStatus(true);
        }
      } else {
        setReplyIndex(null);
        setQueryStatus(false);
        setQueryData({ 'value': '', 'id': '', 'parentID': '' });
      }
      setQuery(updatedQuery);
      setAuthorQuery(updatedQuery); // Update the authorQuery state
      setRender(!render);
    } catch (error) {
      handleError(error, 'Error updating response');
    }
  };

  // const handleSaveAQ = (commentData) => {
  //     const cleanContent = commentData.comment
  //       .replace(/<(?!\/?(b|i|u|strong|em))[^>]*>/g, "")
  //       .replace(/&nbsp;/g, " ")
  //       .trim();

  //     if (!cleanContent) {
  //       toastError('Query content cannot be empty');
  //       return;
  //     }

  //     // Get next query number
  //     const sortedQueries = query.sort((a, b) => {
  //       const numA = parseInt(a.label.replace(/\D/g, ''));
  //       const numB = parseInt(b.label.replace(/\D/g, ''));
  //       return numB - numA;
  //     });

  //     const lastNumber = sortedQueries.length > 0 ? 
  //       parseInt(sortedQueries[0].label.replace(/\D/g, '')) : 0;
  //     const nextNumber = `Q${lastNumber + 1}`;

  //     const newQuery = {
  //       Username: userProfile.username,
  //       role: userProfile.role,
  //       ActivityID: userProfile.ActivityID,
  //       label: nextNumber,
  //       Date_Time: new Date().toLocaleString(),
  //       query: cleanContent,
  //       wholematch: `\\AQ{${nextNumber}}{${cleanContent}}`,
  //       attachment: commentData.fileattached || [],
  //       replies: [],
  //       aqCheck: true,
  //       linenumber: props.cursorPosition // Get cursor position from props
  //     };

  //     setQuery([...query, newQuery]);
  //     setAuthorQuery([...query, newQuery]);
  //   };
  // const handleSaveAQ = (commentData) => {
  //     const cursorPosition = props.cursorPosition || viewDoc?.state?.selection?.main?.from;

  //     if (!cursorPosition) {
  //       toastError('Please select a position in the text to add query');
  //       return false;
  //     }

  //     const cleanContent = commentData.comment
  //       .replace(/<(?!\/?(b|i|u|strong|em))[^>]*>/g, "")
  //       .replace(/&nbsp;/g, " ")
  //       .trim();

  //     if (!cleanContent) {
  //       toastError('Query content cannot be empty');
  //       return false;
  //     }

  //     // Extract content from AQ tag
  //     const contentMatch = cleanContent.match(/\\AQ(?:\[\])?\{Q\d+\}\{(.*?)\}/);
  //     if (!contentMatch) {
  //       toastError('Invalid AQ tag format');
  //       return false;
  //     }

  //     const extractedContent = contentMatch[1];
  //     const qNumberMatch = cleanContent.match(/Q(\d+)/);
  //     const currentQNumber = qNumberMatch[1];

  //     // Check for duplicate numbers
  //     const isDuplicateNumber = query.some(q => {
  //       const existingNumber = q.label.match(/Q(\d+)/)?.[1];
  //       return existingNumber === currentQNumber;
  //     });

  //     if (isDuplicateNumber) {
  //       toastError(`Question number Q${currentQNumber} already exists`);
  //       return false;
  //     }

  //     const newQuery = {
  //       Username: userProfile.username,
  //       role: userProfile.role,
  //       ActivityID: userProfile.ActivityID,
  //       label: `Q${currentQNumber}`,
  //       Date_Time: new Date().toLocaleString(),
  //       query: extractedContent, // Use extracted content for display
  //       wholematch: cleanContent, // Keep full AQ tag for editor
  //       attachment: commentData.fileattached || [],
  //       replies: [],
  //       aqCheck: true,
  //       linenumber: viewDoc.state.doc.lineAt(cursorPosition).number,
  //       from: cursorPosition,
  //       to: cursorPosition,
  //       selectText: viewDoc.state.sliceDoc(
  //         viewDoc.state.selection.main.from,
  //         viewDoc.state.selection.main.to
  //       )
  //     };

  //     // Update editor content
  //     const transaction = viewDoc.state.update({
  //       changes: {
  //         from: cursorPosition,
  //         to: cursorPosition,
  //         insert: newQuery.wholematch
  //       }
  //     });
  //     viewDoc.dispatch(transaction);

  //     setQuery([...query, newQuery]);
  //     setAuthorQuery([...query, newQuery]);
  //     viewDoc.focus();
  //     return true;
  //   };
  function isYesNoQuestion(sentence) {
    // Define regex patterns for yes/no questions
    const yesNoPatterns = [/^(is|are|do|does|did|can|could|will|would|should|has|have|am|was|were)\b/i];
    // Check if the sentence matches any of the yes/no patterns
    return yesNoPatterns.some(pattern => pattern.test(sentence));
  }
  // const handleSaveAQ = (commentData) => {
  //     const cursorPosition = props.cursorPosition || viewDoc?.state?.selection?.main?.from;

  //     if (!cursorPosition) {
  //         toastError('Please select a position in the text to add query');
  //         return false;
  //     }

  //     const cleanContent = commentData.comment
  //         .replace(/<(?!\/?(b|i|u|strong|em))[^>]*>/g, "")
  //         .replace(/&nbsp;/g, " ")
  //         .trim();

  //     if (!cleanContent) {
  //         toastError('Query content cannot be empty');
  //         return false;
  //     }

  //     // Get next query number
  //     const sortedQueries = query.sort((a, b) => {
  //         const numA = parseInt(a.label.replace(/\D/g, ''));
  //         const numB = parseInt(b.label.replace(/\D/g, ''));
  //         return numB - numA;
  //     });

  //     const lastNumber = sortedQueries.length > 0 ? 
  //         parseInt(sortedQueries[0].label.replace(/\D/g, '')) : 0;
  //     const nextNumber = `Q${lastNumber + 1}`;

  //     // Check if it's a yes/no question
  //     const isYesNo = isYesNoQuestion(cleanContent);

  //     // Format the AQ tag
  //     const formattedContent = isYesNo ? 
  //         `\\AQ[]{${nextNumber}}{${cleanContent}}` : 
  //         `\\AQ{${nextNumber}}{${cleanContent}}`;

  //     const newQuery = {
  //         Username: userProfile.username,
  //         role: userProfile.role,
  //         ActivityID: userProfile.ActivityID,
  //         label: nextNumber,
  //         Date_Time: new Date().toLocaleString(),
  //         query: cleanContent,
  //         wholematch: formattedContent,
  //         attachment: commentData.fileattached || [],
  //         replies: [],
  //         aqCheck: isYesNo,
  //         linenumber: viewDoc.state.doc.lineAt(cursorPosition).number,
  //         from: cursorPosition,
  //         to: cursorPosition,
  //         selectText: viewDoc.state.sliceDoc(
  //             viewDoc.state.selection.main.from,
  //             viewDoc.state.selection.main.to
  //         )
  //     };

  //     // Update editor content
  //     const transaction = viewDoc.state.update({
  //         changes: {
  //             from: cursorPosition,
  //             to: cursorPosition,
  //             insert: formattedContent
  //         }
  //     });
  //     viewDoc.dispatch(transaction);

  //     setQuery([...query, newQuery]);
  //     setAuthorQuery([...query, newQuery]);
  //     viewDoc.focus();
  //     return true;
  // };

  // const handleSaveAQ = (commentData) => {
  //     const cursorPosition = props.cursorPosition || viewDoc?.state?.selection?.main?.from;

  //     if (!cursorPosition) {
  //         toastError('Please select a position in the text to add query');
  //         return false;
  //     }

  //     const cleanContent = commentData.comment
  //         .replace(/<(?!\/?(b|i|u|strong|em))[^>]*>/g, "")
  //         .replace(/&nbsp;/g, " ")
  //         .trim();

  //     if (!cleanContent) {
  //         toastError('Query content cannot be empty');
  //         return false;
  //     }

  //     const sortedQueries = query.sort((a, b) => {
  //         const numA = parseInt(a.label.replace(/\D/g, ''));
  //         const numB = parseInt(b.label.replace(/\D/g, ''));
  //         return numB - numA;
  //     });

  //     const lastNumber = sortedQueries.length > 0 ? 
  //         parseInt(sortedQueries[0].label.replace(/\D/g, '')) : 0;
  //     const nextNumber = `Q${lastNumber + 1}`;

  //     const isYesNo = isYesNoQuestion(cleanContent);

  //     const formattedContent = isYesNo ? 
  //         `\\AQ[]{${nextNumber}}{${cleanContent}}` : 
  //         `\\AQ{${nextNumber}}{${cleanContent}}`;

  //     const newQuery = {
  //         Username: userProfile.username,
  //         role: userProfile.role,
  //         ActivityID: userProfile.ActivityID,
  //         label: nextNumber,
  //         Date_Time: new Date().toLocaleString(),
  //         query: cleanContent,
  //         wholematch: formattedContent,
  //         attachment: commentData.fileattached || [],
  //         replies: [],
  //         aqCheck: isYesNo,
  //         linenumber: viewDoc.state.doc.lineAt(cursorPosition).number,
  //         from: cursorPosition,
  //         to: cursorPosition,
  //         selectText: viewDoc.state.sliceDoc(
  //             viewDoc.state.selection.main.from,
  //             viewDoc.state.selection.main.to
  //         )
  //     };

  //     const transaction = viewDoc.state.update({
  //         changes: {
  //             from: cursorPosition,
  //             to: cursorPosition,
  //             insert: formattedContent
  //         }
  //     });
  //     viewDoc.dispatch(transaction);

  //     setQuery([...query, newQuery]);
  //     setAuthorQuery([...query, newQuery]);
  //     viewDoc.focus();
  //     return true;
  // };
  const handleSaveAQ = (commentData) => {
    const cursorPosition = props.cursorPosition || viewDoc?.state?.selection?.main?.from;

    if (!cursorPosition) {
      toastError('Please select a position in the text to add query');
      return false;
    }

    const cleanContent = commentData.comment
      .replace(/<(?!\/?(b|i|u|strong|em))[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (!cleanContent) {
      toastError('Query content cannot be empty');
      return false;
    }

    // Sort queries by label number in descending order
    const sortedQueries = query.sort((a, b) => {
      const numA = parseInt(a.label.replace(/\D/g, ''));
      const numB = parseInt(b.label.replace(/\D/g, ''));
      return numB - numA;
    });

    // Get format from the most recent query (first element after sorting)
    const lastUsedFormat = sortedQueries.length > 0 ?
      sortedQueries[0].wholematch.match(/\\AQ(\[.*?\])?{.*?}{.*?}/) : null;

    const hasOptionalParam = lastUsedFormat && lastUsedFormat[0].includes('[');

    const nextNumber = `Q${parseInt(sortedQueries[0]?.label.replace(/\D/g, '') || '0') + 1}`;
    const isYesNo = isYesNoQuestion(cleanContent);

    // Use the latest format pattern
    const formattedContent = hasOptionalParam ?
      `\\AQ[]{${nextNumber}}{${cleanContent}}` :
      `\\AQ{${nextNumber}}{${cleanContent}}`;

    const newQuery = {
      Username: userProfile.username,
      role: userProfile.role,
      ActivityID: userProfile.ActivityID,
      label: nextNumber,
      Date_Time: new Date().toLocaleString(),
      query: cleanContent,
      wholematch: formattedContent,
      attachment: commentData.fileattached || [],
      replies: [],
      aqCheck: isYesNo,
      linenumber: viewDoc.state.doc.lineAt(cursorPosition).number,
      from: cursorPosition,
      to: cursorPosition,
      selectText: viewDoc.state.sliceDoc(
        viewDoc.state.selection.main.from,
        viewDoc.state.selection.main.to
      )
    };

    const transaction = viewDoc.state.update({
      changes: {
        from: cursorPosition,
        to: cursorPosition,
        insert: formattedContent
      }
    });
    viewDoc.dispatch(transaction);

    setQuery([...query, newQuery]);
    setAuthorQuery([...query, newQuery]);
    viewDoc.focus();
    return true;
  };


  const fileProgress = (value) => {
    setCommentProgress(value)
  }

  // Staged attachment UI (type dropdown + alt-text option + file preview),
  // shared by the reply and edit-reply areas. Mirrors the comment tab.
  const renderAttachmentStaging = () =>
    isFileUploaded && selectedFiles.length > 0 ? (
      <>
        <select
          value={attachmentTypes || ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'mediaAttachment') {
              const invalidFile = selectedFiles.find((f) => !isValidMediaFile(f.name));
              if (invalidFile) {
                toastError(`"${invalidFile.name}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                return;
              }
            }
            if (value === 'imageAttachment') {
              const invalidFile = selectedFiles.find((f) => !isValidImageFile(f.name));
              if (invalidFile) {
                toastError(`"${invalidFile.name}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                return;
              }
            }
            setAttachmentTypes(value);
            setAltTextGeneration(''); // reset alt-text when type changes
            setImageAction('');
            setReplaceImageName('');
            setMediaAction('');
            setReplaceMediaName('');
          }}
          required
          className="attachment-type-dropdown"
          aria-label="Attachment type selection for type of file"
        >
          <option value="">Select Type</option>
          <option value="imageAttachment">Image</option>
          <option value="textAttachment">Text</option>
          <option value="mediaAttachment">Media</option>
          <option value="otherAttachment">Other</option>
        </select>

        {/* New Image / Replace Image choice. Custom listbox (not a native
            <select>) so the labels can be bold. */}
        {attachmentTypes === 'imageAttachment' && (() => {
          const imageActionOptions = [
            { value: 'new', label: 'New Image', description: 'Adding a new figure entirely (never existed before)' },
            { value: 'replace', label: 'Replace Image', description: "Swapping an existing figure's file (updates a current one)" },
          ];
          const currentOption = imageActionOptions.find(opt => opt.value === imageAction);
          return (
            <div className="att-form-group" style={{ marginTop: '8px' }}>
              <div className="att-custom-select" ref={imageTypeDropdownOpen ? imageTypeDropdownRef : null}>
                <button
                  type="button"
                  className="att-custom-select-trigger"
                  aria-label="Image type selection"
                  onClick={() => setImageTypeDropdownOpen((prev) => !prev)}
                >
                  <span>
                    {currentOption ? (
                      <><strong>{currentOption.label}</strong> – {currentOption.description}</>
                    ) : '-- Choose an option --'}
                  </span>
                  <FaChevronDown className="att-custom-select-arrow" />
                </button>
                {imageTypeDropdownOpen && (
                  <ul className="att-custom-select-list" role="listbox">
                    {imageActionOptions.map((opt) => {
                      const isSelected = imageAction === opt.value;
                      return (
                        <li
                          key={opt.value}
                          role="option"
                          aria-selected={isSelected}
                          tabIndex={0}
                          className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            setImageAction(opt.value);
                            setReplaceImageName('');
                            setImageTypeDropdownOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setImageAction(opt.value);
                              setReplaceImageName('');
                              setImageTypeDropdownOpen(false);
                            }
                          }}
                        >
                          <strong>{opt.label}</strong> – {opt.description}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          );
        })()}

        {/* Existing image name picker, only when "Replace Image" is chosen */}
        {attachmentTypes === 'imageAttachment' && imageAction === 'replace' && (
          <div className="att-form-group" style={{ marginTop: '8px' }}>
            <div className="att-custom-select" ref={replaceDropdownOpen ? replaceDropdownRef : null}>
              <button
                type="button"
                className="att-custom-select-trigger"
                onClick={() => setReplaceDropdownOpen((prev) => !prev)}
              >
                <span>{getImageDisplayTitle(replaceImageName) || '-- Select image --'}</span>
                <FaChevronDown className="att-custom-select-arrow" />
              </button>
              {replaceDropdownOpen && (
                <ul className="att-custom-select-list" role="listbox">
                  {[...imageFileList]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                    .map((img, imgIndex) => (
                      <li
                        key={imgIndex}
                        role="option"
                        aria-selected={replaceImageName === img.name}
                        tabIndex={0}
                        className={`att-custom-select-option ${replaceImageName === img.name ? 'selected' : ''}`}
                        onClick={() => {
                          setReplaceImageName(img.name);
                          setReplaceDropdownOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setReplaceImageName(img.name);
                            setReplaceDropdownOpen(false);
                          }
                        }}
                      >
                        {getImageDisplayTitle(img.name)}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* New File / Replace File choice */}
        {attachmentTypes === 'mediaAttachment' && (
          <div className="att-form-group" style={{ marginTop: '8px' }}>
            <select
              value={mediaAction}
              onChange={(e) => {
                setMediaAction(e.target.value);
                setReplaceMediaName('');
              }}
              required
              className="att-select"
              aria-label="File type selection"
            >
              <option value="">-- Choose an option --</option>
              <option value="new">New File</option>
              <option value="replace">Replace File</option>
            </select>
          </div>
        )}

        {/* Existing supplementary file name picker, only when "Replace File" is chosen */}
        {attachmentTypes === 'mediaAttachment' && mediaAction === 'replace' && (
          <div className="att-form-group" style={{ marginTop: '8px' }}>
            <div className="att-custom-select" ref={replaceDropdownOpen ? replaceDropdownRef : null}>
              <button
                type="button"
                className="att-custom-select-trigger"
                onClick={() => setReplaceDropdownOpen((prev) => !prev)}
              >
                <span>{replaceMediaName || '-- Select file --'}</span>
                <FaChevronDown className="att-custom-select-arrow" />
              </button>
              {replaceDropdownOpen && (
                <ul className="att-custom-select-list" role="listbox">
                  {[...supplementaryFileList]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                    .map((suppFile, suppIndex) => (
                      <li
                        key={suppIndex}
                        role="option"
                        aria-selected={replaceMediaName === suppFile.name}
                        tabIndex={0}
                        className={`att-custom-select-option ${replaceMediaName === suppFile.name ? 'selected' : ''}`}
                        onClick={() => {
                          setReplaceMediaName(suppFile.name);
                          setReplaceDropdownOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setReplaceMediaName(suppFile.name);
                            setReplaceDropdownOpen(false);
                          }
                        }}
                      >
                        {suppFile.name}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Alt-text option — shown only for image attachments when enabled */}
        {alttextEnabled && attachmentTypes === 'imageAttachment' && (
          <div className="att-form-group" style={{ marginTop: '8px' }}>
            <div className="att-alt-section">
              <div className="att-alt-header">
                Alternative text option <span className="att-required">*</span>
              </div>
              <div className="att-alt-body">
                <label className={`att-alt-option ${altTextGeneration === 'yes' ? 'att-selected' : ''}`}>
                  <input
                    type="radio"
                    name="aq-alt-text"
                    value="yes"
                    checked={altTextGeneration === 'yes'}
                    onChange={() => setAltTextGeneration('yes')}
                    required
                  />
                  <div className="att-alt-option-text">
                    <strong>Add/replace with a completely new image</strong>
                    <span>A new alt-text needs to be generated.</span>
                  </div>
                </label>

                <label className={`att-alt-option ${altTextGeneration === 'no' ? 'att-selected' : ''}`}>
                  <input
                    type="radio"
                    name="aq-alt-text"
                    value="no"
                    checked={altTextGeneration === 'no'}
                    onChange={() => setAltTextGeneration('no')}
                  />
                  <div className="att-alt-option-text">
                    <strong>Upload the same image in higher resolution</strong>
                    <span>Retain the previously generated alt-text</span>
                  </div>
                </label>

                <div className="att-info-banner">
                  <svg className="att-info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#0c5aa8" />
                  </svg>
                  <span>If only the resolution changed (low to high), new alt text may not be required.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="comment-attached">
          {selectedFiles.map((item, idx) => (
            <div className="comment-attached-list" key={idx} aria-label="file attachment with reply">
              <p title={item.name}>{item.name}</p>
              <label>size - {item.size}</label>
              <button aria-label="Remove attachment" onClick={() => resetAttachmentStaging()}>
                <FaRegTrashAlt />
              </button>
            </div>
          ))}
        </div>
      </>
    ) : null;

  return (
    <div className="comment-area authorytab">
      <div className={`modal-outer ${commentProgress ? 'active' : ''}`}>
        <div className="modal confirmation-popup">
          <div className="modal-header">
            <h1>Uploading</h1>
          </div>
          <div className="modal-body">
            <p>Uploading file... Please wait while we save your comment.</p>
            <span className="loader"></span>
          </div>
        </div>
      </div>
      <header className="slider-header">
        <h2>
          Author Query - ({answerCount}/{query.length})
        </h2>
    <div style={{ width: "20px" }}>
      <button
        className="close-btn"
        title="Close"
        tabIndex={0}
        ref={closeBtnRef}
        onClick={() => {
          setSidebarClose(true);
          // Return focus to the Author Query tab (right sidebar thirdstep button)
          setTimeout(() => {
            const authorTab = document.querySelector('.thirdstep');
            if (authorTab) authorTab.focus();
          }, 0);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSidebarClose(true);
            setTimeout(() => {
              const authorTab = document.querySelector('.thirdstep');
              if (authorTab) authorTab.focus();
            }, 0);
          }
        }}
      >
        <FaTimes />
      </button>
    </div>
      </header>
      <main className="comment-group">
        {userProfile.role !== "Author" &&
          userProfile.role !== "Editor" &&
          userProfile.role !== "Master Copier" && (
            <>
              <div className="comment-note">
                  <FaFileAlt />{" "}
                  <p>Note: Please select a word or text to insert a query</p>
                </div>
                <CommentBox
                  onSaveComment={handleSaveAQ}
                  onCancelComment={() => { }}
                  getTriggerfile={props.getTrigger}
                  fileProgress={fileProgress}
                  // refreshAttachments={props.refreshAttachments}
                />
            </>
          )}
        {/* <div className="query-input-container">
                <input 
                    type="text" 
                    className="query-input"
                    placeholder="Type your query here..."
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            const queryText = e.target.value;
                            if (queryText.trim()) {
                                props.onAddQuery(queryText);
                                e.target.value = '';
                            }
                        }
                    }}
                />
            </div> */}
        <section className="comment-group author-query">
          {query.length === 0 ? <NodataFound /> : ""}
          {query.map((item, index) => (
            <article
              className={
                item.repliesMark ||
                  item.answerCheck === "yes" ||
                  (item.answerCheck === "no" && item.replies?.length > 0) ||
                  item.replied ||
                  item.reviewed ||
                  item.verified
                  ? "comment-inner replied"
                  : "comment-inner"
              }
              key={index}
              onClick={() => queryScroll(item)}
              // tabIndex={0}
            >
              {/* <h5>{item.Role} <label>{item.Date}</label></h5> */}
              {editingIndex === index ? (
                <textarea
                  value={editedQuery}
                  onChange={(e) => setEditedQuery(e.target.value)}
                />
              ) : (
                <p
                  style={{wordBreak:"break-word"}}
                  aria-label={`A ${item.label}: ${item.query}`}
                  tabIndex={0}
                  role="article"
                >
                  A{item.label}: {item.query}
                </p>
              )}
              {item.replies &&
                [...new Set(item.replies.map(JSON.stringify))]
                  .map((str) => JSON.parse(str))
                  .map((reply, idx) => (
                    <>
                      {idx !== queryData.id &&
                        queryData.parentID !== index &&
                        item.answerCheck !== "yes" ? (
                        <div
                          className="reply-group"
                          key={`reply-${item.label}-${idx}`}
                        >
                          {idx !== queryData.id &&
                            queryData.parentID !== index &&
                            item.answerCheck !== "yes" && (
                              <div key={idx} className="reply-content">
                                {/* <p>Reply {idx + 1}:</p> */}
                                <p>Reply from {reply.role} :</p>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: reply.content,
                                  }}
                                ></div>
                                {/* {userProfile?.role === reply.role && (
                                                <div className="reply-footer">                                                   <button onClick={() => editReply(index, idx)} title="Edit" className="edit">
                                                    <FaEdit />
                                                </button>
                                                    <button onClick={() => replyDelete(index, idx)} title="Delete" className="detele">
                                                        <FaRegTrashAlt />
                                                    </button>
                                                </div>
                                            )} */}
                              </div>
                            )}
                          {userProfile?.role === "Author" && (
                            <div className="reply-footer">
                              <button
                                onClick={() => editReply(index, idx)}
                                title="Edit"
                                className="edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => replyDelete(index, idx)}
                                title="Delete"
                                className="detele"
                              >
                                <FaRegTrashAlt />
                              </button>
                            </div>
                          )}
                          {/* Show edit/delete only for Editor's own replies */}
                          {userProfile?.role === "Editor" &&
                            reply.role === "Editor" && (
                              <div className="reply-footer">
                                <button
                                  onClick={() => editReply(index, idx)}
                                  title="Edit"
                                  className="edit"
                                  tabIndex={0}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  onClick={() => replyDelete(index, idx)}
                                  title="Delete"
                                  className="detele"
                                  tabIndex={0}
                                >
                                  <FaRegTrashAlt />
                                </button>
                              </div>
                            )}
                          {/* Show reply button for Editor only when there are no Editor replies yet */}
                          {userProfile?.role === "Editor" &&
                            !item.replies.some(
                              (r) => r.role === "Editor"
                            ) && (
                              <div className="comment-footer">
                                <Button
                                  className="accept-btn-fixedContrast"
                                  onClick={() => handleReply(index)}
                                  title="Reply"
                                  tabIndex={0}
                                  // ref={closeBtnRef}
                                >
                                  <FaReply /> Reply
                                </Button>
                              </div>
                            )}
                        </div>
                      ) : (
                        ""
                      )}
                      <>
                        {queryStatus &&
                          index === queryData.parentID &&
                          idx === queryData.id ? (
                          <div className="reply-area">
                            <div className="reply-btns">
                              <div>
                                <Button onClick={() => applyStyle("bold")} title="Bold" tabIndex={0}><FaBold /></Button>
                                <Button onClick={() => applyStyle("italic")} title="Italic" tabIndex={0}><FaItalic /></Button>
                                <Button onClick={() => applyStyle("underline")} title="Underline" tabIndex={0}><FaUnderline /></Button>
                                <Button onClick={() => applyStyle("superscript")} title="SuperScript" tabIndex={0}><FaSuperscript /></Button>
                                <Button onClick={() => applyStyle("subscript")} title="SubScript" tabIndex={0}><FaSubscript /></Button>
                                {/* {item.attachment.length !== 0 ? */}
                                <Button className="attchcomment-btn" title="Attachment" tabIndex={0}>
                                  <FaPaperclip />
                                  <input type="file" onChange={(e) => handleUpload(e)} ref={fileInputRef}></input>
                                </Button>
                                {/* : ""} */}
                              </div>
                             <div>
                              <Button onClick={handleCancel} tabIndex={0}>
                                <span style={{
                                  position: 'absolute',
                                  width: 1,
                                  height: 1,
                                  padding: 0,
                                  margin: -1,
                                  overflow: 'hidden',
                                  clip: 'rect(0,0,0,0)',
                                  border: 0
                                }}>Cancel</span>
                                <FaTimes />
                              </Button>
                            </div>

                            </div>
                            <div className="CommentContent"  onClick={(e) => e.stopPropagation()}>
                              <div
                                contentEditable="true"
                                id="TypeContentEdit"
                                className="CommentTextEntry UIFont Cmdtxtcontent"
                                dangerouslySetInnerHTML={{
                                  __html: queryData.value || "",
                                }}
                              />
                            </div>
                            {renderAttachmentStaging()}
                            {loader ? (
                              <div className="fileupload-loder">
                                <span className="loader"></span>
                              </div>
                            ) : (
                              ""
                            )}
                            <div className="reply-footer">
                              <Button
                                className="save-btn-fixedContrast"
                                onClick={() => handleReplySaved(index, queryData.id)}
                                tabIndex={0}
                              >
                                <FaSave /> Save
                              </Button>
                              <Button
                                className="cancel-btn"
                                onClick={editClose}
                                tabIndex={0}
                              >
                                <FaTimes /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                      </>
                    </>
                  ))}
              {item.attachment !== undefined ? (
                <div
                  className="comment-attached"
                  style={{ marginTop: "6px" }}
                >
                  {item.attachment.map((file, a) =>
                    file !== null && file !== undefined ? (
                      <div className="comment-attached-list" key={a}>
                        <p>{file?.Filename}</p>
                        <label>size - {file?.Filesize}</label>
                        {userProfile.role === JSON.parse(file.Rolename)[0].RoleName ? 
                        <button className="buttondelete" onClick={() => deleteConfirm(file, a, index)} tabIndex={0}>
                          <FaRegTrashAlt />
                        </button>: ""}
                      </div>
                    ) : (
                      ""
                    )
                  )}
                </div>
              ) : (
                ""
              )}
              {replyIndex === index && (
                <div className="reply-area">
                  <div className="reply-btns">
                    <div>
                      <Button ref={replyBtnRef} onClick={() => applyStyle("bold")} title="Bold" tabIndex={0}><FaBold /></Button>
                      <Button onClick={() => applyStyle("italic")} title="Italic" tabIndex={0}><FaItalic /></Button>
                      <Button onClick={() => applyStyle("underline")} title="Underline" tabIndex={0}><FaUnderline /></Button>
                      <Button onClick={() => applyStyle("superscript")} title="SuperScript" tabIndex={0}><FaSuperscript /></Button>
                      <Button onClick={() => applyStyle("subscript")} title="SubScript" tabIndex={0}><FaSubscript /></Button>
                      {/* {item.attachment.length === 0 ? */}
                      <Button className="attchcomment-btn" title="Attachment" tabIndex={0}>
                        <FaPaperclip />
                        <input type="file" onChange={(e) => handleUpload(e)} ref={fileInputRef}></input>
                      </Button>
                      {/* : ""} */}
                    </div>
                    <div>
                     <Button onClick={handleCancel}>
                      <span style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        border: 0
                      }}>Cancel</span>
                      <FaTimes />
                    </Button>
                    </div>
                  </div>
                  <div className="CommentContent"  onClick={(e) => e.stopPropagation()}> 
                    <div
                      contentEditable="true"
                      id="TypeContent"
                      ref={replyContentRef}
                      className="CommentTextEntry UIFont Cmdtxtcontent"
                    ></div>
                  </div>
                  {renderAttachmentStaging()}
                  {loader ? (
                    <div className="fileupload-loder">
                      <span className="loader"></span>
                    </div>
                  ) : (
                    ""
                  )}
                  <div className="reply-footer">
                    <Button
                      id="TypeContent"
                      className="save-btn-fixedContrast"
                      title="Post"
                      onClick={() => handleReplySave(index)}
                    >
                      <FaPaperPlane /> Post
                    </Button>
                  </div>
                </div>
              )}
              {!queryStatus ? (
                <div className="comment-footer1">
                  {editingIndex === index ? (
                    <>
                      <Button
                        className="save-btn"
                        title="Save"
                        onClick={() =>
                          handleSave(
                            item.linenumber,
                            item.wholematch,
                            editedQuery
                          )
                        }
                      >
                        <FaSave /> Save
                      </Button>
                      <Button className="cancel-btn" onClick={handleCancel} title="Cancel">
                        <FaTimes /> Cancel
                      </Button>
                    </>
                  ) : item.reviewOnly ? (
                    <div
                      className="checkbox-area"
                      style={{
                        display: item.replies?.length > 0 ? "none" : "block",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="disabled-input"
                        id={`reviewcheck-${index}`}
                        name="reviewcheck"
                        value="reviewcheck"
                        onChange={() => handleReviewed(index)}
                        disabled={
                          userProfile?.role === "Editor" || userProfile?.role === "Master Copier" ? true : false
                        }
                        checked={item.reviewed === true}
                      />
                      <label htmlFor={`reviewcheck-${index}`}>
                        Reviewed
                      </label>
                    </div>
                  ) : item.altTextQuery ? (
                    <>
                      <div
                        className="checkbox-area"
                        style={{
                          display: item.replies?.length > 0 ? "none" : "block",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="disabled-input"
                          id={`verifiedcheck-${index}`}
                          name="verifiedcheck"
                          value="verifiedcheck"
                          onChange={() => handleVerified(index)}
                          disabled={
                            userProfile?.role === "Editor" || userProfile?.role === "Master Copier" ? true : false
                          }
                          checked={item.verified === true}
                        />
                        <label htmlFor={`verifiedcheck-${index}`}>
                          Verified
                        </label>
                      </div>
                      <Button
                        className="accept-btn-clrContrast"
                        disabled={
                          item.verified === true || // Disable once verified
                          item.replies?.length > 0 // Disable if already has replies
                        }
                        style={{
                          display:
                            item.replies?.length > 0 || item.verified === true
                              ? "none"
                              : "inline-block",
                        }}
                        onClick={() => handleReply(index)}
                        tabIndex={0}
                        {...(index === query.length - 1 ? {
                          onKeyDown: e => {
                            if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              setTimeout(() => {
                                const commentTab = document.getElementById('comment-tab-btn');
                                if (commentTab) commentTab.focus();
                              }, 0);
                            }
                          }
                        } : {})}
                      >
                        <FaReply /> Reply
                      </Button>
                    </>
                  ) : (
                    <>
                      {item.aqCheck ? (
                        <div className="radio-buttons" style={{
                          display:
                            item.replies?.length > 0 ? "none" : "flex",
                        }}>
                          <div>
                            <input
                              type="radio"
                              className="disabled-input"
                              name={"aqchecker" + index}
                              id="yes"
                              value="yes"
                              checked={item.answerCheck === "yes"}
                              disabled={
                                userProfile?.role === "Editor" || userProfile?.role === "Master Copier" ? true : false
                              }
                              onChange={(e) => handleRadio(e, index)}
                            />
                            <label htmlFor={"aqchecker" + index}> Yes</label>
                          </div>
                          {userProfile?.role !== "Editor" && userProfile?.role !== "Master Copier" && (
                            <div>
                              <input
                                type="radio"
                                className="readonlyNoDisable"
                                name={"aqchecker" + index}
                                id="no"
                                value="no"
                                checked={item.answerCheck === "no"}
                                onChange={(e) => handleRadio(e, index)}
                              />
                              <label className="readonlyNoDisable" htmlFor={"aqchecker" + index}> No</label>
                            </div>
                          )}

                        </div>
                      ) : (
                        <div
                          className="checkbox-area"
                          style={{
                            display:
                              item.replies?.length > 0 ? "none" : "block",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="disabled-input"
                            id={`querycheck-${index}`}
                            name="querycheck"
                            value="querycheck"
                            onChange={() => handleMark(index)}
                            disabled={
                              userProfile?.role === "Editor" || userProfile?.role === "Master Copier" ? true : false
                            }
                            checked={item.repliesMark}
                          />
                          <label htmlFor={`querycheck-${index}`}>
                            No action required
                          </label>
                        </div>
                      )}
                      <Button
                        className="accept-btn-clrContrast"
                        disabled={
                          item.repliesMark || // Disable if replies are marked
                          item.answerCheck === "yes" || // Disable if answerCheck is 'yes'
                          (item.aqCheck && item.answerCheck === "yes") || // Disable for 'yes' radio in aqCheck
                          item.replies?.length > 0 // Disable if already has replies
                        }
                        style={{
                          display: item.aqCheck
                            ? "none" // Show/hide based on showReplyButton for aqCheck
                            : item.replies?.length > 0 ||
                              item.repliesMark ||
                              item.answerCheck === "yes"
                              ? "none"
                              : "inline-block", // Default display logic
                        }}
                        onClick={() => handleReply(index)}
                           tabIndex={0}
                        {...(index === query.length - 1 ? {
                          onKeyDown: e => {
                            if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              setTimeout(() => {
                                const commentTab = document.getElementById('comment-tab-btn');
                                if (commentTab) commentTab.focus();
                              }, 0);
                            }
                          }
                        } : {})}
                        // title="Reply"
                      >
                        <FaReply /> Reply
                      </Button>
                      {/* <Button className="reject-btn" onClick={() => handleDelete(item.linenumber, item.wholematch)} disabled> <FaTimes /> Delete</Button>
                                        <Button className="edit-btn" onClick={() => handleEdit(index, item.query)} disabled><FaEdit /> Edit</Button> */}
                    </>
                  )}
                </div>
              ) : (
                ""
              )}
            </article>
          ))}
        </section>
      </main>
    </div>
  );
};
export default QueryTab;